// Date helpers for the maintenance task forms.
//
// `<input type="date">` produces a YYYY-MM-DD string in the user's local
// calendar. Naively concatenating `T00:00:00Z` interprets that as UTC midnight
// — for users west of UTC, the saved date appears to roll backward by a day
// when read back. These helpers translate between the local-date string and
// an ISO instant that represents *local midnight* of the chosen day.

/**
 * Convert a YYYY-MM-DD string from a `<input type="date">` to an ISO 8601
 * instant representing local midnight of that day.
 *
 *   localDateToISO('2026-04-25') // in UTC-7 → '2026-04-25T07:00:00.000Z'
 *
 * Returns null for empty/invalid input.
 */
export const localDateToISO = (yyyymmdd) => {
    if (!yyyymmdd || typeof yyyymmdd !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyymmdd);
    if (!m) return null;
    const [, y, mo, d] = m;
    const dt = new Date(Number(y), Number(mo) - 1, Number(d));
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
};

/**
 * Convert a server-supplied ISO datetime back to a YYYY-MM-DD string in the
 * user's local calendar. Pairs with `localDateToISO` so a round-trip preserves
 * the date the user picked.
 *
 *   isoToLocalDate('2026-04-25T07:00:00.000Z') // in UTC-7 → '2026-04-25'
 *
 * Returns '' for empty/invalid input.
 */
export const isoToLocalDate = (iso) => {
    if (!iso) return '';
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '';
    const y = dt.getFullYear();
    const mo = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${mo}-${d}`;
};

/** Today's date as YYYY-MM-DD in the user's local calendar. */
export const todayLocalDate = () => isoToLocalDate(new Date().toISOString());

/**
 * The first scheduled payment date for a loan, derived from its origination
 * date. Payments conventionally begin the first of the month following the
 * loan's first ~30 days:
 *   first payment = first day of the month after (origination + 30 days)
 *
 *   firstPaymentDate('2020-06-25') // → '2020-08-01'
 *
 * UTC math keeps it free of local-timezone drift. Returns a YYYY-MM-01 string,
 * or null for missing/invalid input.
 */
export const firstPaymentDate = (originationDate) => {
    if (!originationDate || typeof originationDate !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(originationDate);
    if (!m) return null;
    const [, y, mo, d] = m.map(Number);
    // Reject impossible calendar dates (e.g. 2026-13-01, 2026-02-31) up front —
    // otherwise Date.UTC silently normalizes them into a wrong date.
    const parsed = new Date(Date.UTC(y, mo - 1, d));
    if (parsed.getUTCFullYear() !== y || parsed.getUTCMonth() !== mo - 1 || parsed.getUTCDate() !== d) {
        return null;
    }
    // origination + 30 days, then the first day of the month AFTER that date.
    const plus30 = new Date(Date.UTC(y, mo - 1, d + 30));
    const first = new Date(Date.UTC(plus30.getUTCFullYear(), plus30.getUTCMonth() + 1, 1));
    const yy = first.getUTCFullYear();
    const mm = String(first.getUTCMonth() + 1).padStart(2, '0');
    return `${yy}-${mm}-01`;
};

/**
 * Mortgage maturity date derived from the origination date and term.
 *
 * A `term_months`-payment loan matures on its *final* payment — which is
 * term_months − 1 months after the first one (the first payment is payment #1,
 * so payment #term is term − 1 months later, not term):
 *   first payment = first day of the month following (origination + 30 days)
 *   maturity      = first payment + (term_months − 1)
 *
 *   mortgageMaturityDate('2020-06-25', 360) // → '2050-07-01'
 *
 * UTC math keeps it free of local-timezone drift. Returns a YYYY-MM-DD string,
 * or null for missing/invalid input.
 */
export const mortgageMaturityDate = (originationDate, termMonths) => {
    // Strict: Number() (not parseInt) rejects "12.5"/"360abc" instead of
    // silently truncating them into a wrong maturity date.
    const term = Number(termMonths);
    if (!Number.isInteger(term) || term <= 0) return null;
    const first = firstPaymentDate(originationDate);
    if (!first) return null;
    const [fy, fmo] = first.split('-').map(Number);
    // The maturity (final) payment is term − 1 months after the first payment.
    const maturity = new Date(Date.UTC(fy, fmo - 1 + (term - 1), 1));
    const yy = maturity.getUTCFullYear();
    const mm = String(maturity.getUTCMonth() + 1).padStart(2, '0');
    return `${yy}-${mm}-01`;
};

/**
 * The next monthly payment date, assuming payments fall on the 1st: the first
 * day of the month on or after `from` (today by default).
 *
 *   nextFirstOfMonth(new Date(2025, 4, 15)) // → '2025-06-01'
 *   nextFirstOfMonth(new Date(2025, 4, 1))  // → '2025-05-01'
 *
 * Returns a YYYY-MM-DD string.
 */
export const nextFirstOfMonth = (from = new Date()) => {
    const base = from.getDate() === 1
        ? new Date(from.getFullYear(), from.getMonth(), 1)
        : new Date(from.getFullYear(), from.getMonth() + 1, 1);
    const yy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, '0');
    return `${yy}-${mm}-01`;
};

/**
 * The expected next due date for a loan, given the latest statement's due date.
 *
 * When we have a real `latestDueDate` (the most recent statement's due_date,
 * which is past/current), the next payment is the first of the month AFTER it.
 * With no statements yet we fall back to the first-of-month estimate from today.
 * Either way the result lands on the 1st, matching the loan-payment convention.
 *
 *   expectedNextDueDate('2026-05-01') // → '2026-06-01'
 *   expectedNextDueDate('2026-12-15') // → '2027-01-01'
 *   expectedNextDueDate(null)         // → nextFirstOfMonth() (today-based)
 *
 * Returns a YYYY-MM-01 string.
 */
export const expectedNextDueDate = (latestDueDate) => {
    if (typeof latestDueDate === 'string') {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(latestDueDate);
        if (m) {
            const [, y, mo, d] = m.map(Number);
            const parsed = new Date(Date.UTC(y, mo - 1, d));
            if (parsed.getUTCFullYear() === y && parsed.getUTCMonth() === mo - 1 && parsed.getUTCDate() === d) {
                // First of the month AFTER the latest due date (mo is 1-based, so
                // Date.UTC(y, mo, 1) is the first of the following month).
                const next = new Date(Date.UTC(y, mo, 1));
                const yy = next.getUTCFullYear();
                const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
                return `${yy}-${mm}-01`;
            }
        }
    }
    return nextFirstOfMonth();
};
